import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCapabilities } from '../hooks/useCapabilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteProject, fetchActiveRuns, fetchProject, fetchProjectPullRequests } from '../api';
import { useBoardDeepLink } from '../hooks/useBoardDeepLink';
import { UnderlineTabs } from '../components/Tabs';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PageHeader } from '../components/PageHeader';
import { Badge } from '../components/Badge';
import { Chip } from '../components/Chip';
import { IssuesPanel } from '../components/IssuesPanel';
import { MigrateIssuesDialog } from '../components/MigrateIssuesDialog';
import { OverflowMenu } from '../components/OverflowMenu';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { RunTaskModal } from '../components/RunTaskModal';
import { ProjectTransferZone } from '../components/ProjectTransferZone';
import { ExternalLink, GitPullRequestArrow, MessageSquare, Play, Plus, Trash2 } from 'lucide-react';
import { SectionDivider } from '../components/SectionDivider';
import { ErrorState, LoadingState } from '../components/States';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';

export function ProjectPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const { can, isWorkspaceOwner } = useCapabilities();
  const canManage = can('project:manage');
  const canRunTask = can('run:trigger');
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [runTaskOpen, setRunTaskOpen] = useState(false);
  const [bulkSelecting, setBulkSelecting] = useState(false);
  const isMobile = useIsMobile();

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });

  // Keep the URL on the pretty code ("/projects/OMHB"), never the UUID.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  // Internally we always key data + mutations off the resolved UUID.
  const resolvedId = projectQuery.data?.id ?? projectId;

  const deleteMutation = useMutation({
    mutationFn: () => deleteProject(resolvedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projects });
      navigate(wp('/'));
    },
  });

  const activeRunsQuery = useQuery({
    queryKey: qk.activeRuns,
    queryFn: fetchActiveRuns,
    refetchInterval: 3000,
  });

  const activeRuns = (activeRunsQuery.data ?? []).filter(
    (r) => r.projectId === resolvedId,
  );
  const activeCount = activeRuns.length;
  // A single active chat (issue-less) run has no issue page to link to.
  const activeIssueRef =
    activeCount === 1 ? activeRuns[0].issueCode ?? activeRuns[0].issueId : null;
  const activeTo = activeIssueRef ? `/issues/${activeIssueRef}` : `/`;

  const pullRequestsQuery = useQuery({
    queryKey: qk.projectPullRequests(resolvedId),
    queryFn: () => fetchProjectPullRequests(resolvedId),
    enabled: !!projectQuery.data,
    refetchInterval: 5000,
  });
  const pullRequests = pullRequestsQuery.data;
  // The tab belongs to one project: switching project lands on Issues again.
  type Tab = 'issues' | 'prs';
  const [tabState, setTabState] = useState<{ key: string; tab: Tab }>({ key: resolvedId, tab: 'issues' });
  const tab: Tab = tabState.key === resolvedId ? tabState.tab : 'issues';
  const setTab = (t: Tab) => setTabState({ key: resolvedId, tab: t });
  const tabsRef = useRef<HTMLDivElement>(null);
  const showTab = (t: Tab) => {
    setTab(t);
    tabsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  // ?issues=<state> (Inbox, Needs-you links) opens the board on that filter.
  const board = useBoardDeepLink(!!projectQuery.data, () => showTab('issues'));

  useDocumentTitle(projectQuery.data?.name);

  if (projectQuery.isLoading) return <LoadingState label="Loading project…" />;
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  // The handle to use in URLs — the pretty code when present, else the UUID.
  const ref = project.code ?? project.id;

  // A prototype is a Project too, but it lives under /prototypes — never render
  // it through the normal project page (its delete/issues behave differently).
  if (project.kind === 'prototype') {
    return <Navigate to={wp(`/prototypes/${ref}`)} replace />;
  }

  const nav = projectNav(project);

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: project.name },
        ]}
      />

      <PageHeader
        title={
          project.code ? (
            <>
              <span className="mr-2 font-mono text-base font-medium uppercase tracking-wide text-ink-faint">
                {project.code}
              </span>
              {project.name}
            </>
          ) : (
            project.name
          )
        }
        badges={
          <span className="flex items-center gap-1.5">
            {!project.hasGithubCredential && (
              <Link to="/settings" title="Add a GitHub token in Settings to enable clone/push/PR">
                <Badge tone="warning">no token</Badge>
              </Link>
            )}
            {activeCount > 0 && (
              <Link to={wp(activeTo)}>
                <Badge tone="brand" dot pulse>
                  {`${activeCount} running`}
                </Badge>
              </Link>
            )}
          </span>
        }
        subtitle={
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <a
              href={project.repoUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex min-w-0 max-w-full items-center gap-1.5 font-mono text-signal hover:text-signal"
            >
              <span className="truncate">{project.repoFullName}</span>
              <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
            </a>
            <Chip mono>branch: {project.defaultBranch}</Chip>
          </span>
        }
        actions={
          <>
            {/* Section navigation lives in ProjectSectionNav below the header. */}
            {can('chat:post', { projectId: project.id }) && (
              <Link
                to={wp(`/projects/${ref}/chat`)}
                className="btn-secondary hidden sm:inline-flex"
              >
                <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
                Chat with PM
              </Link>
            )}
            {canRunTask && (
              <button
                type="button"
                className="btn-secondary hidden sm:inline-flex"
                onClick={() => setRunTaskOpen(true)}
              >
                <Play className="h-3.5 w-3.5" aria-hidden="true" />
                Run a task
              </button>
            )}
            {can('issue:create', { projectId: project.id }) && (
              <Link
                to={wp(`/projects/${ref}/issues/new`)}
                className="btn-primary hidden sm:inline-flex"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                New issue
              </Link>
            )}
            <OverflowMenu
              label="More project actions"
              items={[
                // On phones the header keeps only this menu, so the actions
                // that lost their inline buttons move in here.
                ...(isMobile && can('chat:post', { projectId: project.id })
                  ? [
                      {
                        label: 'Chat with PM',
                        icon: <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />,
                        onClick: () => navigate(wp(`/projects/${ref}/chat`)),
                      },
                    ]
                  : []),
                ...(isMobile && canRunTask
                  ? [
                      {
                        label: 'Run a task',
                        icon: <Play className="h-3.5 w-3.5" aria-hidden="true" />,
                        onClick: () => setRunTaskOpen(true),
                      },
                    ]
                  : []),
                {
                  label: 'Migrate GitHub issues',
                  icon: (
                    <GitPullRequestArrow className="h-3.5 w-3.5" aria-hidden="true" />
                  ),
                  onClick: () => setMigrateOpen(true),
                },
              ]}
            />
          </>
        }
      />

      <ProjectSectionNav
        kind={project.kind}
        projectRef={ref}
        active="overview"
        newIssueTo={
          !bulkSelecting && can('issue:create', { projectId: project.id })
            ? wp(`/projects/${ref}/issues/new`)
            : undefined
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete project?"
        confirmLabel="Delete project"
        busy={deleteMutation.isPending}
        error={
          deleteMutation.isError
            ? errorMessage(deleteMutation.error, 'Could not delete project')
            : null
        }
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmDelete(false)}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">{project.name}</span>{' '}
            and <span className="font-medium">all of its issues, run
            history, and loops</span>? This cannot be undone.
          </>
        }
      />

      <div ref={tabsRef}>
        <UnderlineTabs
          ariaLabel="Project sections"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'issues', label: 'Issues' },
            { value: 'prs', label: `Pull requests${pullRequests ? ` · ${pullRequests.length}` : ''}` },
          ]}
        />
      </div>
      {tab === 'issues' ? (
        <IssuesPanel
          key={board.panelKey}
          initialStatus={board.status}
          projectId={project.id}
          kind={project.kind}
          projectRef={ref}
          onSelectionModeChange={setBulkSelecting}
        />
      ) : pullRequests && pullRequests.length > 0 ? (
        <ul className="divide-y divide-line rounded-card border border-line bg-surface-1">
          {pullRequests.map((pr) => (
            <li key={pr.url}>
              <a href={pr.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-surface-2">
                {pr.issueCode && <span className="font-mono text-2xs text-ink-faint">{pr.issueCode}</span>}
                <span className="truncate text-ink">{pr.title}</span>
                {pr.number && <span className="ml-auto font-mono text-2xs text-ink-muted">#{pr.number}</span>}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-card border border-line bg-surface-2 px-4 py-6 text-center text-sm text-ink-muted">No open pull requests.</p>
      )}

      <RunTaskModal
        open={runTaskOpen}
        onClose={() => setRunTaskOpen(false)}
        projectId={project.id}
      />

      <MigrateIssuesDialog
        open={migrateOpen}
        onClose={() => setMigrateOpen(false)}
        projectId={project.id}
        hasCredential={project.hasGithubCredential}
      />

      {/* Deleting a project is owner-only; members never see the danger zone. */}
      {canManage && (
        <>
          <SectionDivider label="Danger zone" tone="danger" />

          {/* Transfer is for owners of the source workspace only. */}
          {isWorkspaceOwner && !project.isSelf && (
            <ProjectTransferZone
              project={{ id: project.id, name: project.name, kind: project.kind, ref }}
            />
          )}

          <div className="card flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">Delete this project</p>
              <p className="text-xs text-ink-faint">
                Permanently removes all issues, run history, and loops. This cannot be undone.
              </p>
            </div>
            <button
              type="button"
              className="btn-danger-ghost"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}
